import { gql } from './graphql';
import { YardSpotLatestHistory } from './yardCheck';

// Raw shape of a row from flux's `yard_observations_decisions` audit table. The
// json columns arrive as parsed objects; `evidence.read.*` holds the RTLS read
// for source = "rtls" rows.
// Only the read fields the Exceptions tab queries (see DECISION_FIELDS).
export type DecisionRtlsHistory = Pick<
  YardSpotLatestHistory,
  | 'id'
  | 'timestamp'
  | 'verdict'
  | 'matchConfidence'
  | 'trailerNumberExpected'
  | 'trailerNumberOcrReading'
  | 'trailerNumberOcrConfidenceScore'
  | 'trailerBrandExpected'
  | 'trailerBrandOcrReading'
  | 'croppedImgUrl'
  | 'processedImgUrl'
  | 'annotatedCropUrl'
  | 'lat'
  | 'lon'
  | 'deviceHeading'
  | 'deviceOrientation'
> & {
  /** The full frame with annotations — the uncropped counterpart of annotatedCropUrl. */
  annotatedImgUrl: string | null;
  /** The truck the camera rides on; null on reads from before Ziggy sent it. */
  spotter: { name?: string | null; uuid?: string | null } | null;
  /** Ziggy's report page for the read; null on reads from before Ziggy sent it. */
  debugUrl: string | null;
  /** Ziggy's conclusion on the read; `outcome` is confirmed, fuzzy_agree, empty or conflict. */
  funnel: { outcome?: string | null } | null;
  /**
   * Where Ziggy read the trailer: `type` is yard_spot, dock or zone_range, with
   * the spot's or door's name and its zone's. A zone range names no zone here.
   */
  location: {
    type?: string | null;
    yard_spot_name?: string | null;
    facility_zone_name?: string | null;
    dock_name?: string | null;
    warehouse_zone_name?: string | null;
    range?: { lo?: string | null; hi?: string | null } | null;
  } | null;
};

export interface YardObservationDecision {
  id: string;
  source: string;
  feed: string | null;
  facilityId: number | null;
  locationKind: string | null;
  trailerUuid: string | null;
  trailerNumber: string | null;
  scac: string | null;
  yardSpotUuid: string | null;
  yardSpotName: string | null;
  dockUuid: string | null;
  observedAt: string | null;
  evidence: Record<string, any> | string | null;
  currentLocation: Record<string, any> | string | null;
  mode: string;
  decision: string;
  reason: string;
  score: number | null;
  movementType: string | null;
  sourceFacilityId: number | null;
  destinationFacilityId: number | null;
  taskUuid: string | null;
  actor: string | null;
  error: string | null;
  insertedAt: string;
  /** A plain-English account of the decision: what RTLS read, where flux had the trailer, and what was done and why. */
  explanation?: string | null;
  /** Facts a hold saved: for a ghost trailer, the trailer flux has, its carrier's `scac` and where else it is active. */
  explanationContext?: Record<string, any> | string | null;
  /** The rtls_history read this decision was made from; null for other sources or a deleted read. */
  rtlsHistory: DecisionRtlsHistory | null;
  /**
   * The exception this decision is the newest hold of. Only the exceptions
   * query asks for it; absent on audit-log rows.
   */
  exception?: YardObservationException | null;
  /**
   * flux's exception type for this row — stale_yms_empty_spot,
   * different_location, ghost_trailer or other — or null when it raises none.
   */
  exceptionType?: string | null;
  /**
   * Set only on a person's correction (feed `manual_corrections`, source
   * `manual`): what kind it was — `new_trailer` — who asked for it (their
   * public user id), and the exception it answers.
   */
  correction?: string | null;
  requestedBy?: string | null;
  answersExceptionId?: string | null;
}

/**
 * One thing the feed and flux disagree about, across every read that has held
 * it. Its `id` is stable while it stays open; the decision row it is served as
 * changes with each new read.
 */
export interface YardObservationException {
  id: string;
  status: string;
  /** stale_yms_empty_spot, different_location, ghost_trailer or other. */
  type: string;
  reason: string;
  holdCount: number;
  firstDecisionId: string | null;
  openedAt: string;
  lastSeenAt: string;
  /** When it was resolved; null unless resolved. */
  resolvedAt?: string | null;
}

export interface YardObservationExceptionsResponse {
  openCount: number;
  exceptions: YardObservationDecision[];
}

const DECISION_FIELDS = `
  id
  source
  feed
  facilityId
  locationKind
  trailerUuid
  trailerNumber
  scac
  yardSpotUuid
  yardSpotName
  dockUuid
  observedAt
  evidence
  currentLocation
  mode
  decision
  reason
  score
  movementType
  sourceFacilityId
  destinationFacilityId
  taskUuid
  actor
  error
  insertedAt
  explanation
  explanationContext
  correction
  requestedBy
  answersExceptionId
  exceptionType
  rtlsHistory {
    id
    timestamp
    verdict
    matchConfidence
    trailerNumberExpected
    trailerNumberOcrReading
    trailerNumberOcrConfidenceScore
    trailerBrandExpected
    trailerBrandOcrReading
    croppedImgUrl
    processedImgUrl
    annotatedCropUrl
    annotatedImgUrl
    lat
    lon
    deviceHeading
    deviceOrientation
    spotter
    debugUrl
    funnel
    location
  }
`;

const EXCEPTIONS_BY_FACILITY = `
  query YardObservationExceptionsByFacility($facilityId: ID!, $hours: Int!) {
    yardObservationExceptionsByFacility(
      facilityId: $facilityId
      hours: $hours
    ) {
      openCount
      exceptions {
        ${DECISION_FIELDS}
        exception {
          id
          status
          type
          reason
          holdCount
          firstDecisionId
          openedAt
          lastSeenAt
          resolvedAt
        }
      }
    }
  }
`;

const AUDIT_LOG_BY_FACILITY = `
  query YardObservationAuditLogByFacility($facilityId: ID!, $hours: Int!) {
    yardObservationAuditLogByFacility(
      facilityId: $facilityId
      hours: $hours
    ) {
      ${DECISION_FIELDS}
    }
  }
`;

// Both queries take `hours` (required, at least 1): only rows from the last
// this-many hours — exceptions by when last seen, audit-log decisions by when
// recorded. The exceptions query's `openCount` uses the same window.

export async function fetchYardObservationExceptions(
  facilityId: string | number,
  hours: number
): Promise<YardObservationExceptionsResponse> {
  const data = await gql<{
    yardObservationExceptionsByFacility: YardObservationExceptionsResponse | null;
  }>(EXCEPTIONS_BY_FACILITY, { facilityId, hours });
  return data.yardObservationExceptionsByFacility ?? { openCount: 0, exceptions: [] };
}

export async function fetchYardObservationAuditLog(
  facilityId: string | number,
  hours: number
): Promise<YardObservationDecision[]> {
  const data = await gql<{
    yardObservationAuditLogByFacility: YardObservationDecision[] | null;
  }>(AUDIT_LOG_BY_FACILITY, { facilityId, hours });
  return data.yardObservationAuditLogByFacility ?? [];
}

// STUB: flux has no mutations for these yet, so they resolve without touching
// the backend. The UI applies their result to local state only, which means
// an action does not survive a refresh. Replace with real mutations (create
// move / undo) when they exist.
const stubDelay = <T>(value: T, ms = 150): Promise<T> =>
  new Promise((resolve) => setTimeout(() => resolve(value), ms));

export const createReconciliationMove = (_decisionId: string): Promise<void> =>
  stubDelay(undefined);

export const undoAuditAction = (_decisionId: string): Promise<void> =>
  stubDelay(undefined);

export interface IgnoredYardObservationException {
  id: string;
  status: string;
  ignoredBy: string | null;
  ignoredAt: string | null;
}

const IGNORE_YARD_OBSERVATION_EXCEPTION = `
  mutation IgnoreYardObservationException($id: ID!) {
    ignoreYardObservationException(id: $id) {
      id
      status
      ignoredBy
      ignoredAt
    }
  }
`;

/**
 * Ignores an open exception, by the exception's own id (`YardException.exceptionId`
 * — the stable id of the thing being disagreed about) — not the decision id a
 * card or audit-log row is otherwise keyed by. Throws (via `gql`) with the
 * server's message — "Only an open exception can be ignored" or "Exception
 * not found" — if it isn't open or doesn't exist.
 */
export async function ignoreException(
  exceptionId: string
): Promise<IgnoredYardObservationException> {
  const data = await gql<{
    ignoreYardObservationException: IgnoredYardObservationException;
  }>(IGNORE_YARD_OBSERVATION_EXCEPTION, { id: exceptionId });
  return data.ignoreYardObservationException;
}

export type TrailerLoadStatus = 'empty' | 'loaded' | 'loading' | 'unloading';

/**
 * A trailer the campus already has that a ghost trailer answer may mean: the
 * same number (`exact`), or failing any of those, the same digits with letters
 * dropped from both (`digits`).
 */
export interface GhostTrailerMatch {
  trailerUuid: string;
  trailerNumber: string | null;
  /** Null when flux could not read its carrier. */
  scac: string | null;
  facilityId: number | null;
  /** Where flux has it; null for none. */
  locationType: PlacementType | null;
  locationUuid: string | null;
  locationName: string | null;
  /** The zone the dock (warehouse zone) or spot (facility zone) is in; null for a zone. */
  zoneName: string | null;
  match: 'exact' | 'digits';
}

export interface CreateUnknownTrailerResult {
  /** Null when `possibleMatches` stopped the create. */
  trailerUuid: string | null;
  exception: {
    id: string;
    status: string;
    reason: string;
    holdCount: number;
    lastSeenAt: string;
    resolvedAt: string | null;
  };
  /**
   * The correction as flux recorded it — a decision on the `manual_corrections`
   * feed — or null when nothing was recorded (facility not activated).
   */
  decision: YardObservationDecision | null;
  /**
   * Trailers the campus already has that the number may mean. When any,
   * nothing was created and the exception is unchanged: answer with
   * `resolveGhostTrailerWithMatch`, or create again with `ignoreMatches`.
   */
  possibleMatches: GhostTrailerMatch[];
}

const GHOST_TRAILER_MATCH_FIELDS = `
  trailerUuid
  trailerNumber
  scac
  facilityId
  locationType
  locationUuid
  locationName
  zoneName
  match
`;

const CREATE_UNKNOWN_TRAILER = `
  mutation CreateUnknownTrailer(
    $id: ID!
    $scac: String!
    $trailerNumber: String
    $loadStatus: String
    $locationType: String
    $locationUuid: ID
    $notes: String
    $ignoreMatches: Boolean
  ) {
    createUnknownTrailer(
      id: $id
      scac: $scac
      trailerNumber: $trailerNumber
      loadStatus: $loadStatus
      locationType: $locationType
      locationUuid: $locationUuid
      notes: $notes
      ignoreMatches: $ignoreMatches
    ) {
      trailerUuid
      possibleMatches {
        ${GHOST_TRAILER_MATCH_FIELDS}
      }
      exception {
        id
        status
        reason
        holdCount
        lastSeenAt
        resolvedAt
      }
      decision {
        ${DECISION_FIELDS}
      }
    }
  }
`;

/** Where flux places the trailer, as it names the kinds of place. */
export type PlacementType = 'dock' | 'yard_spot' | 'facility_zone';

export interface CreateUnknownTrailerInput {
  /** The exception's own id (`YardException.exceptionId`), not the decision id. */
  exceptionId: string;
  scac: string;
  /** Omitted: the number the camera read. */
  trailerNumber?: string;
  /** Omitted: a new trailer is created EMPTY, an existing one keeps its load. */
  loadStatus?: TrailerLoadStatus;
  /**
   * Where to place it instead of where the read saw it; must be at the read's
   * facility. Omitted: the read's own place.
   */
  location?: { type: PlacementType; uuid: string } | null;
  /** Written on the trailer's lineage once it is placed. */
  notes?: string | null;
  /** The person saw `possibleMatches` and said none is theirs: skip the check. */
  ignoreMatches?: boolean;
}

/**
 * Creates the trailer an unknown-trailer exception saw, and places it — where
 * the person chose, or where the read saw it. Unless `ignoreMatches`, flux
 * first checks the campus for that number and, finding any, creates nothing
 * and returns them as `possibleMatches`. Safe to retry on a held
 * exception: flux finds the trailer it already created and resubmits the
 * correction rather than making a duplicate. Throws (via `gql`) with the
 * server's message when it refuses outright — e.g. "That location is not at
 * the facility the trailer was read at".
 */
export async function createUnknownTrailer({
  exceptionId,
  scac,
  trailerNumber,
  loadStatus,
  location,
  notes,
  ignoreMatches
}: CreateUnknownTrailerInput): Promise<CreateUnknownTrailerResult> {
  const data = await gql<{ createUnknownTrailer: CreateUnknownTrailerResult }>(
    CREATE_UNKNOWN_TRAILER,
    {
      id: exceptionId,
      scac,
      trailerNumber,
      loadStatus,
      locationType: location?.type,
      locationUuid: location?.uuid,
      notes,
      ignoreMatches
    }
  );
  return data.createUnknownTrailer;
}

export interface ResolvedYardObservationException {
  id: string;
  status: string;
  resolvedAt: string | null;
  resolvedBy: string | null;
  matchedTrailerUuid: string | null;
}

const RESOLVE_GHOST_TRAILER_WITH_MATCH = `
  mutation ResolveGhostTrailerWithMatch($id: ID!, $trailerUuid: ID!) {
    resolveGhostTrailerWithMatch(id: $id, trailerUuid: $trailerUuid) {
      id
      status
      resolvedAt
      resolvedBy
      matchedTrailerUuid
    }
  }
`;

/**
 * Resolves a ghost trailer exception with one of `createUnknownTrailer`'s
 * `possibleMatches`: the person says it is the trailer the camera read. Moves
 * nothing — the trailer stays where flux has it. Throws (via `gql`) with the
 * server's message, e.g. "That trailer is not active on this campus".
 */
export async function resolveGhostTrailerWithMatch(
  exceptionId: string,
  trailerUuid: string
): Promise<ResolvedYardObservationException> {
  const data = await gql<{
    resolveGhostTrailerWithMatch: ResolvedYardObservationException;
  }>(RESOLVE_GHOST_TRAILER_WITH_MATCH, { id: exceptionId, trailerUuid });
  return data.resolveGhostTrailerWithMatch;
}
